import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';
import { storableError } from '../../util/errors';
import { parse } from '../../util/urlHelpers';
import { getAllTransitionsForEveryProcess } from '../../transactions/transaction';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { getWarehouses } from '../ManageWarehousePage/ManageWarehousePage.duck';

const sortedTransactions = txs =>
  reverse(
    sortBy(txs, tx => {
      return tx.attributes ? tx.attributes.lastTransitionedAt : null;
    })
  );

// ================ Action types ================ //

export const FETCH_ORDERS_OR_SALES_REQUEST = 'app/InboxPage/FETCH_ORDERS_OR_SALES_REQUEST';
export const FETCH_ORDERS_OR_SALES_SUCCESS = 'app/InboxPage/FETCH_ORDERS_OR_SALES_SUCCESS';
export const FETCH_ORDERS_OR_SALES_ERROR = 'app/InboxPage/FETCH_ORDERS_OR_SALES_ERROR';

// ================ Reducer ================ //

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const initialState = {
  fetchInProgress: false,
  fetchOrdersOrSalesError: null,
  pagination: null,
  transactionRefs: [],
};

export default function inboxPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_ORDERS_OR_SALES_REQUEST:
      return { ...state, fetchInProgress: true, fetchOrdersOrSalesError: null };
    case FETCH_ORDERS_OR_SALES_SUCCESS: {
      // const transactions = sortedTransactions(payload.data.data);
      const transactions = payload.data.data;
      return {
        ...state,
        fetchInProgress: false,
        transactionRefs: entityRefs(transactions),
        pagination: payload.data.meta,
      };
    }
    case FETCH_ORDERS_OR_SALES_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchInProgress: false, fetchOrdersOrSalesError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchOrdersOrSalesRequest = () => ({ type: FETCH_ORDERS_OR_SALES_REQUEST });
const fetchOrdersOrSalesSuccess = response => ({
  type: FETCH_ORDERS_OR_SALES_SUCCESS,
  payload: response,
});
const fetchOrdersOrSalesError = e => ({
  type: FETCH_ORDERS_OR_SALES_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

import { loadData as loadTransactionData } from '../TransactionPage/TransactionPage.duck';

const INBOX_PAGE_SIZE = 10;

/* Transitions that leave a transaction in a "this is a question" state.
 *
 * Both inquiry-process transitions and the inquire transition of the purchase and
 * booking processes: a buyer asking about a product listing has not ordered
 * anything. On WHEELPLACE Test that is 24 `inquire` plus 17
 * `inquire-without-payment`, and a seller thinks of all 41 as questions. */
export const QUESTION_TRANSITIONS = [
  'transition/inquire',
  'transition/inquire-without-payment',
  'transition/mark-reopened',
  'transition/mark-handled',
];

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const { tab, id } = params;

  const getUnReadMessagesKey = (tab) => {
    if (tab === 'orders') {
      apiQueryParams.meta_customerHasUnreadMessages = true
    }
    else if (tab === 'sales') {
      apiQueryParams.meta_providerHasUnreadMessages = true
    }
  }

  const getReadMessagesKey = (tab) => {
    if (tab === 'orders') {
      apiQueryParams.meta_customerHasUnreadMessages = false
    }
    else if (tab === 'sales') {
      apiQueryParams.meta_providerHasUnreadMessages = false
    }
  }

  const onlyFilterValues = {
    orders: 'order',
    sales: 'sale',
  };

  const onlyFilter = onlyFilterValues[tab];
  if (!onlyFilter) {
    return Promise.reject(new Error(`Invalid tab for InboxPage: ${tab}`));
  }

  dispatch(fetchOrdersOrSalesRequest());

  const { listingId, page = 1, filter, state, search:urlSearch } = parse(search);

  const apiQueryParams = {
    // listingId:listingId,
    only: onlyFilter,
    // lastTransitions: getAllTransitionsForEveryProcess(),
    include: [
      'listing',
      'provider',
      'provider.profileImage',
      'customer',
      'customer.profileImage',
      'booking',
      'messages.sender',
    ],
    'fields.transaction': [
      'processName',
      'lastTransition',
      'lastTransitionedAt',
      'transitions',
      'payinTotal',
      'payoutTotal',
      'lineItems',
      'protectedData',
      'metadata',
    ],
    // The three category fields build the breadcrumb listing URL on the
    // listing links — see util/listingSeoUrl.js.
    'fields.listing': [
      'title',
      'availabilityPlan',
      'publicData.listingType',
      'publicData.warehouse',
      'publicData.adType',
      'publicData.categoryLevel1',
      'publicData.categoryLevel2',
    ],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
    page,
    perPage: INBOX_PAGE_SIZE,
    sort: 'meta_lastMessageDate,lastMessageAt,lastTransitionedAt',
  };

  /* `hasMessage: true` was applied to EVERY inbox query, and that is what makes
   * BR-8's "Hanterad" filter "currently broken and always empty".
   *
   * A buyer's first contact from a listing page is stored in
   * `protectedData.inquiryMessage`, NOT as a message resource, so a thread nobody
   * has replied to yet has no messages and was excluded. Measured on WHEELPLACE
   * Test: of 45 inquiry-state transactions, 36 carry a real message and 9 do not.
   * Those 9 questions were invisible in Meddelanden — the seller had been asked
   * something and the inbox said nothing had arrived. Same cause for Hanterad:
   * `states: 'state/handled'` alone returns 2 rows; adding `hasMessage: true`
   * returns 0. The state name was never the problem, contrary to the ticket's
   * note that it "queries a non-existent state".
   *
   * It is kept for the unfiltered list, where it earns its place: without it the
   * list fills with transactions that were never conversations — 100 auto-cancel
   * and 83 expire-payment rows on that same marketplace. It is dropped wherever
   * the filter has already narrowed to threads that ARE conversations, so a
   * message-less question is reachable via Frågor and Hanterad.
   *
   * The row already falls back to the inquiry text for its preview, so these
   * threads render correctly once they are allowed through.
   *
   * Remaining gap, deliberately left: an unanswered question still does not
   * appear under "Alla". Closing that needs either two merged queries with their
   * own pagination, or a queryable marker on the transaction. */
  const filterNarrowsToConversations = ['questions', 'enquiries', 'handled'].includes(filter);
  if (!filterNarrowsToConversations) {
    apiQueryParams.hasMessage = true;
  }
  if (listingId) {
    apiQueryParams.listingId = listingId;
  }
  /* BR-4 — the Frågor / Ordrar split. `enquiries` and `transactions` are kept as
   * aliases: the old commented-out filter links used those names, and so do any
   * bookmarks or links already out there. */
  if (filter === 'questions' || filter === 'enquiries') {
    apiQueryParams.lastTransitions = QUESTION_TRANSITIONS.join(',');
  }
  if (filter === 'orders' || filter === 'transactions') {
    apiQueryParams.lastTransitions = getAllTransitionsForEveryProcess().filter(
      e => !QUESTION_TRANSITIONS.includes(e)
    );
  }
  if (filter === 'unread') {
    getUnReadMessagesKey(tab)
  }
  if (filter === 'read') {
    getReadMessagesKey(tab)
  }
  if (filter === 'new') {
    const now = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(now.getDate() - 7);

    apiQueryParams.createdAtStart = sevenDaysAgo.toISOString();
  }
  if (filter === 'handled') {
    /* Verified against the API: `states: 'state/handled'` is valid and returns
     * rows. The `state/` prefix is required — plain `handled` answers 200 with 0
     * results, which is a silent way to be wrong. */
    apiQueryParams.states = 'state/handled'
  }
  if (state) {
    apiQueryParams.states = state
  }
  if (urlSearch) {
    apiQueryParams.meta_searchData = `has_any:${urlSearch}`;
  }

  const loadInboxData = sdk.transactions.query(apiQueryParams).then(response => {
    dispatch(addMarketplaceEntities(response));
    dispatch(fetchOrdersOrSalesSuccess(response));
    dispatch(getWarehouses());
    return response;
  });

  if (id) {
    const transactionRole = tab === 'orders' ? 'customer' : 'provider';
    const txTab = tab === 'orders' ? 'purchases' : 'orders';
    return Promise.all([
      loadInboxData,
      dispatch(loadTransactionData({ id, transactionRole, tab: txTab }, search, config)),
    ]);
  }

  return loadInboxData.catch(e => {
    dispatch(fetchOrdersOrSalesError(storableError(e)));
    throw e;
  });
};
